import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, At, PaperPlaneTilt, CheckCircle } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import Logo from "@/components/Logo";

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const prefill = params.get("email") || (typeof window !== "undefined" && sessionStorage.getItem("voxly:lastEmail")) || "";
  const [email, setEmail] = useState(prefill);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) toast.error("Too many attempts. Try again in a few minutes.");
      else toast.error(err?.response?.data?.detail || "Couldn't send the link. Try again.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="forgot-password-page">
      <div className="mx-auto max-w-md min-h-screen sm:border-x sm:border-white/5 px-6 py-10 flex flex-col">
        <div className="flex items-center justify-between mb-8">
          <Logo size={36} />
          <Link to="/" data-testid="forgot-back" className="p-2 text-muted-foreground hover:text-foreground rounded-full">
            <ArrowLeft size={18} />
          </Link>
        </div>

        {!sent ? (
          <form onSubmit={submit} className="flex-1 flex flex-col">
            <h1 className="font-heading text-4xl font-black tracking-tight">
              Forgot your<br /><span className="text-primary">password?</span>
            </h1>
            <p className="mt-3 text-sm text-muted-foreground">
              Type the email you signed up with. We'll text-mail you a one-tap reset link. It expires in 60 minutes.
            </p>

            <label className="mt-8 block">
              <span className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Email</span>
              <div className="mt-1.5 relative">
                <At size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  data-testid="forgot-email-input"
                  type="email"
                  autoFocus
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full bg-secondary border border-white/10 rounded-2xl pl-10 pr-3 py-3.5 text-base focus:outline-none focus:border-primary"
                />
              </div>
            </label>

            <button
              type="submit"
              disabled={submitting || !email}
              data-testid="forgot-submit"
              className="mt-6 w-full rounded-full bg-primary text-primary-foreground py-4 text-base font-semibold flex items-center justify-center gap-2 glow-hover disabled:opacity-40"
            >
              <PaperPlaneTilt size={18} weight="fill" />
              {submitting ? "Sending…" : "Email me a reset link"}
            </button>

            <p className="mt-4 text-center text-xs text-muted-foreground">
              Remembered it? <Link to="/" className="text-primary font-semibold">Back to sign in</Link>
            </p>

            <div className="mt-auto pt-10 text-[11px] text-muted-foreground/80">
              <p className="font-mono uppercase tracking-widest mb-1">Forgot your email too?</p>
              <p>Email <span className="text-foreground">rekan@voxly.app</span> with any old usernames, your country, and the approximate signup date. We'll find your account manually.</p>
            </div>
          </form>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-center" data-testid="forgot-success">
            <CheckCircle size={64} weight="fill" className="text-emerald-500" />
            <h1 className="mt-6 font-heading text-3xl font-black tracking-tight">Check your inbox</h1>
            <p className="mt-3 text-sm text-muted-foreground max-w-xs">
              If an account exists for <span className="text-foreground font-semibold">{email}</span>, we just emailed a reset link. It's valid for 60 minutes.
            </p>
            <p className="mt-4 text-[11px] text-muted-foreground/80 max-w-xs">
              Tip: check Spam/Promotions if you don't see it. The sender is <span className="text-foreground">Voxly</span>.
            </p>
            <button
              onClick={() => navigate("/", { replace: true })}
              data-testid="forgot-return-login"
              className="mt-8 px-6 py-2.5 rounded-full bg-primary text-primary-foreground text-sm font-semibold"
            >
              Back to sign in
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
